import { forwardRef, useEffect } from 'react';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Button from '@/trio-ui/Button';
import DatePicker from '@/trio-ui/DatePicker';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import { EmployeeWorkStatus, Ethnicity, PersonGender } from '@/types/api';
import { linkedinUrlSchemaWithoutValidation } from '@/utils/candidate';
import { requiredMessage } from '@/utils/formsValidation';
import { ethnicityOptions, genderOptions, employeeWorkStatusOptions } from '@/utils/staffMember';
import * as Styles from './NewStaffMemberFormModal.styles';

export const staffMemberFormSchema = yup.object({
  firstName: yup.string().required(requiredMessage),
  lastName: yup.string().required(requiredMessage),
  birthdate: yup.date(),
  gender: yup.mixed<PersonGender>().oneOf(genderOptions),
  employeeWorkStatus: yup.mixed<EmployeeWorkStatus>().oneOf(employeeWorkStatusOptions),
  ethnicity: yup.mixed<Ethnicity>().oneOf(ethnicityOptions),
  email: yup.string().email('Invalid e-mail address'),
  workEmail: yup.string().email('Invalid e-mail address'),
  mobilePhone: yup.string(),
  linkedinUrl: linkedinUrlSchemaWithoutValidation.required(requiredMessage),
});

export type FormData = yup.Asserts<typeof staffMemberFormSchema>;

export type NewStaffMemberFormModalComponentProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onSubmit?: (data: Omit<FormData, 'birthdate'> & { birthdate?: string }) => void;
  isLoading?: boolean;
};

const NewStaffMemberFormModal = forwardRef<HTMLDivElement, NewStaffMemberFormModalComponentProps>(
  ({ isOpen = false, onClose, onSubmit, isLoading = false }, ref): JSX.Element => {
    const { handleSubmit, values, handleChange, errors, setFieldValue, touched, resetForm } =
      useFormik<yup.TypeOf<typeof staffMemberFormSchema>>({
        initialValues: {
          firstName: '',
          lastName: '',
          employeeWorkStatus: undefined,
          birthdate: undefined,
          gender: undefined,
          ethnicity: undefined,
          email: '',
          workEmail: '',
          mobilePhone: '',
          linkedinUrl: '',
        },
        validateOnBlur: false,
        validationSchema: staffMemberFormSchema,
        onSubmit: (data) => {
          if (isLoading) return;

          onSubmit?.({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            employeeWorkStatus: data.employeeWorkStatus,
            birthdate: data.birthdate ? format(data.birthdate, 'yyyy-MM-dd') : undefined,
            gender: data.gender,
            ethnicity: data.ethnicity,
            email: data.email || undefined,
            workEmail: data.workEmail || undefined,
            mobilePhone: data.mobilePhone || undefined,
            linkedinUrl: data.linkedinUrl || '',
          });
        },
      });

    useEffect(() => {
      if (isOpen) {
        resetForm();
      }
    }, [isOpen, resetForm]);

    return (
      <Styles.Drawer $isOpen={isOpen} ref={ref}>
        <Styles.Header>
          <Styles.StyledUserInitials
            firstName={values.firstName || 'New'}
            lastName={values.lastName || 'Team Member'}
            size={36}
          />
          <Styles.HeaderTitle>
            {values.firstName || values.lastName
              ? `${values.firstName || ''} ${values.lastName}`
              : 'New Team Member'}
          </Styles.HeaderTitle>
        </Styles.Header>
        <Styles.Scrollable onSubmit={handleSubmit}>
          <InputText
            isLabelAboveField
            label="First Name"
            name="firstName"
            placeholder="New"
            value={values.firstName}
            onChange={handleChange}
            errorMessage={(touched.firstName && errors.firstName) || undefined}
          />
          <InputText
            isLabelAboveField
            label="Last Name"
            placeholder="Team Member"
            name="lastName"
            value={values.lastName}
            onChange={handleChange}
            errorMessage={(touched.lastName && errors.lastName) || undefined}
          />
          <DropDown
            isLabelAboveField
            label="Status"
            options={employeeWorkStatusOptions}
            name="employeeWorkStatus"
            defaultValue={values.employeeWorkStatus}
            errorMessage={(touched.employeeWorkStatus && errors.employeeWorkStatus) || undefined}
            onChangeDropDown={handleChange}
          />
          <DatePicker
            isLabelAboveField
            name="birthdate"
            dateFormat="MM/dd/yyyy"
            maxDate={new Date()}
            label="Birth Date"
            value={values.birthdate}
            onChange={(date) => setFieldValue('birthdate', date)}
            errorMessage={(touched.birthdate && errors.birthdate) || undefined}
          />
          <DropDown
            isLabelAboveField
            label="Gender"
            options={genderOptions}
            name="gender"
            defaultValue={values.gender}
            errorMessage={(touched.gender && errors.gender) || undefined}
            onChangeDropDown={handleChange}
          />
          <DropDown
            isLabelAboveField
            label="Ethnicity"
            options={ethnicityOptions}
            name="ethnicity"
            defaultValue={values.ethnicity}
            errorMessage={(touched.ethnicity && errors.ethnicity) || undefined}
            onChangeDropDown={handleChange}
          />
          <InputText
            isLabelAboveField
            label="Personal Email"
            name="email"
            value={values.email}
            onChange={handleChange}
            errorMessage={(touched.email && errors.email) || undefined}
          />
          <InputText
            isLabelAboveField
            label="Work Email"
            name="workEmail"
            value={values.workEmail}
            onChange={handleChange}
            errorMessage={(touched.workEmail && errors.workEmail) || undefined}
          />
          <InputText
            isLabelAboveField
            label="Phone"
            name="mobilePhone"
            type="number"
            value={values.mobilePhone}
            onChange={handleChange}
            errorMessage={(touched.mobilePhone && errors.mobilePhone) || undefined}
          />
          <InputText
            isLabelAboveField
            label="LinkedIn Link"
            name="linkedinUrl"
            value={values.linkedinUrl}
            onChange={handleChange}
            errorMessage={(touched.linkedinUrl && errors.linkedinUrl) || undefined}
          />
          <Styles.Footer>
            <Button type="submit" disabled={isLoading}>
              Save
            </Button>
            <Button type="button" variantStyle="outline" onClick={onClose}>
              Cancel
            </Button>
          </Styles.Footer>
        </Styles.Scrollable>
      </Styles.Drawer>
    );
  }
);

export default NewStaffMemberFormModal;
